import React, { useState } from 'react';
import { Users, Mail, Upload, Newspaper, LogOut, ChevronDown, ChevronRight, LayoutDashboard, Send, List } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from './ui/sidebar';
import { Button } from './ui/button';

interface MenuItem {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items?: {
    title: string;
    view: string;
  }[];
}

const menuItems: MenuItem[] = [
  {
    title: "Email Users",
    icon: Users,
    items: [
      { title: "User Management", view: "admin" },
      { title: "Company Settings", view: "company" },
    ]
  },
  {
    title: "Email Sender",
    icon: Mail,
    items: [
      { title: "Forms", view: "browser-email" },
      { title: "Form Analytics", view: "form-analytics" },
    ]
  },
  {
    title: "Email Migrator",
    icon: Upload,
    items: [
      { title: "Migration Services", view: "migration" },
      { title: "Email Backup S3 Store", view: "email-backup" },
    ]
  },
  {
    title: "Email Marketer",
    icon: Newspaper,
    items: [
      { title: "Dashboard", view: "newsletter-dashboard" },
      { title: "Email Configuration", view: "email-config" },
      { title: "Create Campaign", view: "create-campaign" },
      { title: "Campaigns List", view: "campaigns-list" },
    ]
  },
];

interface AppSidebarProps {
  currentView: string;
  onViewChange: (view: string) => void;
  onLogout: () => void;
}

export const AppSidebar: React.FC<AppSidebarProps> = ({ currentView, onViewChange, onLogout }) => {
  const [expandedItems, setExpandedItems] = useState<string[]>(['Email Users', 'Email Marketer']);

  const toggleExpand = (title: string) => {
    setExpandedItems(prev =>
      prev.includes(title)
        ? prev.filter(item => item !== title)
        : [...prev, title]
    );
  };

  return (
    <Sidebar collapsible="none" variant="sidebar">
      <SidebarHeader className="p-4">
        <h2 className="text-lg font-semibold">Dashzap Admin</h2>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Services</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => {
                const isExpanded = expandedItems.includes(item.title);

                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      onClick={() => toggleExpand(item.title)}
                      className="w-full flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <item.icon className="mr-2 h-4 w-4" />
                        <span>{item.title}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </SidebarMenuButton>

                    {isExpanded && item.items && (
                      <SidebarMenuSub>
                        {item.items.map((subItem) => (
                          <SidebarMenuSubItem key={subItem.view}>
                            <SidebarMenuSubButton
                              onClick={() => onViewChange(subItem.view)}
                              isActive={currentView === subItem.view}
                              asChild
                            >
                              <button className="w-full text-left">
                                {subItem.title}
                              </button>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4">
        <Button
          variant="outline"
          onClick={onLogout}
          className="w-full"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
};
